// ============================================================================
//  FeaturedTonightShelf — guest-facing "🏆 Featured tonight" shelf.
//
//  Per the user's brief (May 2026):
//    "Guest Dashboard Featured Shelf — contains featured spins,
//     category label, live wall badge, replay CTA. This creates
//     'I want to get featured too.' without explicitly saying it."
//
//  Re-uses /api/events/{event_id}/best-spins (same source of truth as
//  the Venue Projector + Best Spins Tonight card) but speaks DIRECTLY
//  to guests: "Featured tonight" instead of "Best Spins Tonight",
//  and a Replay CTA on every card so anyone seeing the shelf can
//  watch the moment immediately.
//
//  Hidden until the event has at least one featured spin — the empty
//  state reads as "nothing happening yet" and adds noise to the
//  dashboard, so we just don't render.
// ============================================================================
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_ACCENT = {
  trending:         { color: '#FF6B6B', glow: 'rgba(255,107,107,0.45)' },
  crowd_favourite:  { color: '#D4AF5E', glow: 'rgba(212,175,94,0.45)'  },
  most_replayed:    { color: '#B89762', glow: 'rgba(184,151,98,0.45)'  },
  biggest_reaction: { color: '#FFB84D', glow: 'rgba(255,184,77,0.45)'  },
};

export const FeaturedTonightShelf = ({ eventId }) => {
  const [cats, setCats] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!eventId) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/events/${eventId}/best-spins`);
        if (!cancelled) {
          setCats(r.data?.categories || []);
          setLoaded(true);
        }
      } catch {
        if (!cancelled) setLoaded(true);
      }
    };
    load();
    const t = setInterval(load, 45000);
    return () => { cancelled = true; clearInterval(t); };
  }, [eventId]);

  if (!loaded || cats.length === 0) return null;

  return (
    <motion.section
      data-testid="featured-tonight-shelf"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
      style={{
        padding: 18, borderRadius: 20, marginBottom: 18,
        background: 'linear-gradient(135deg, rgba(20,18,32,0.92) 0%, rgba(32,20,40,0.92) 100%)',
        border: '1px solid rgba(212,175,94,0.22)',
        boxShadow: '0 0 28px rgba(212,175,94,0.18)',
      }}
    >
      <div style={{ marginBottom: 12, display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <h3 style={{
          margin: 0, fontSize: 18, fontWeight: 800,
          color: '#FFFFFF', letterSpacing: '-0.01em',
        }}>
          🏆 Featured tonight
        </h3>
        <span style={{
          fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.5)',
          letterSpacing: '0.18em', textTransform: 'uppercase',
        }}>
          live wall
        </span>
      </div>

      <div style={{
        display: 'grid', gap: 10,
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
      }}>
        {cats.map((cat) => {
          const accent = CATEGORY_ACCENT[cat.category_slug] || CATEGORY_ACCENT.trending;
          return (
            <motion.a
              key={cat.category_slug}
              data-testid={`featured-tonight-${cat.category_slug}`}
              href={cat.video_url || '#'}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ y: -3, scale: 1.01 }}
              transition={{ type: 'spring', stiffness: 350, damping: 22 }}
              style={{
                position: 'relative', overflow: 'hidden',
                borderRadius: 14, padding: 10,
                background: 'rgba(255,255,255,0.03)',
                border: `1px solid ${accent.color}3a`,
                boxShadow: `0 4px 18px ${accent.glow}`,
                textDecoration: 'none', color: 'inherit',
                cursor: 'pointer',
                display: 'flex', flexDirection: 'column', gap: 8,
              }}
            >
              <div style={{
                width: '100%', aspectRatio: '9 / 16', maxHeight: 180,
                background: cat.thumb_url
                  ? `url(${cat.thumb_url}) center/cover no-repeat`
                  : 'linear-gradient(135deg, #1a1626, #2d1f3e)',
                borderRadius: 10, position: 'relative', overflow: 'hidden',
              }}>
                <div style={{
                  position: 'absolute', top: 6, left: 6,
                  padding: '3px 8px', borderRadius: 999,
                  background: `${accent.color}d0`,
                  color: '#0F0F14', fontSize: 9, fontWeight: 800,
                  letterSpacing: '0.04em',
                  boxShadow: `0 0 12px ${accent.glow}`,
                }}>
                  {cat.category_icon} {cat.category_label}
                </div>
                <div style={{
                  position: 'absolute', inset: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'linear-gradient(180deg, transparent 30%, rgba(0,0,0,0.4) 100%)',
                  pointerEvents: 'none',
                }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%',
                    background: 'rgba(0,0,0,0.55)',
                    backdropFilter: 'blur(6px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '1px solid rgba(255,255,255,0.25)',
                  }}>
                    <span style={{ fontSize: 13, color: '#FFFFFF', marginLeft: 1 }}>▶</span>
                  </div>
                </div>
              </div>
              <span style={{
                fontSize: 11, fontWeight: 700, color: '#FFFFFF',
                letterSpacing: '0.04em', textAlign: 'center',
              }}>
                Replay
              </span>
            </motion.a>
          );
        })}
      </div>
    </motion.section>
  );
};

export default FeaturedTonightShelf;
